export default class SubjectFinder {
    constructor(params, appCallback) {
        let options = params;

        if (!options) options = {};

        const { start, startSection, startPage, exclude, onConfirm } = options;

        //= =========================================
        // static
        this.pages = [...document.querySelectorAll(`[data-role="page"]`)];
        this.excludedSections = exclude ? exclude.sections : null;
        this.excludedPages = exclude ? exclude.pages : null;
        this.start = start !== undefined ? start : 0;
        this.startSection = startSection;
        this.startPage = startPage;
        this.onConfirm = onConfirm;

        // set form ids to form nodes
        this.pages.forEach((page) => {
            const currentPage = page;

            if (page.nodeName.match(/form/i)) {
                const { name } = currentPage.dataset;

                currentPage.addEventListener('submit', (e) =>
                    e.preventDefault()
                );

                currentPage.id = name;
            }
        });

        this.steps = [...this.pages];

        this.sections = [...document.querySelectorAll(`[data-role="section"]`)];
        this.sectionsKeyVal = {};
        this.sectionsInfo = {};

        // set what section a page belongs to
        this._updateSectionInfo();

        // nav controls
        this.controls = [...document.querySelectorAll(`[data-nav]`)];
        this.controlsKeyVal = {};

        // keep key/val references to the controls
        this.controls.forEach((control) => {
            const name = control.dataset.nav;

            this.controlsKeyVal[`_${name}`] = control;
        });

        //= ========================================
        // dynamic
        this.current = this.start;
        this.isLast = this.current === this.steps.length - 1;
        this.isFirst = this.current === 0;
        this.navButtons = {};
        this.pause = false;

        const currentPageNum = document.createElement('span');

        this.pageNumber = currentPageNum;
        this.pageNumber.dataset.navActive = true;

        //= ========================================
        // navigation controls
        this.controls.forEach((control) => {
            control.addEventListener(
                'click',
                this[`_${control.dataset.nav}`].bind(this)
            );

            control.addEventListener('click', this._onAction.bind(this));
        });

        if (this.observerCallback) this.observerCallback();

        this.appCallback = appCallback;

        if (this.appCallback) this.appCallback();
    }

    /// ///////////////////////////////////////
    // -- public

    validate() {
        if (this.active.nodeName.toLowerCase() !== 'form') return;

        const formErrorMsg = document.querySelector(`[data-role="form-error"]`);
        const data = new FormData(this.active);
        const required = [...this.active.querySelectorAll('[required]')];
        const requiredNames = required.map((input) => {
            return input.name;
        });
        const empty = [];

        if (requiredNames.length) {
            for (const key of requiredNames) {
                if (!data.get(key)) {
                    empty.push(key);
                }
            }
        }

        if (empty.length) {
            this.paused(true);

            document.addEventListener('change', this._globalInputValidate);
            document.addEventListener('keyup', this._globalInputValidate);

            empty.forEach((emptyName) => {
                const inputs = [
                    ...document.querySelectorAll(`[name="${emptyName}"]`),
                ];

                inputs.forEach((input) => {
                    if (input.nodeName.match(/input/i)) {
                        switch (input.type) {
                            case 'radio':
                            case 'checkbox':
                                if (!input.checked) {
                                    empty.push(input);
                                    input.parentNode.classList.add(
                                        'subject-finder__form-error'
                                    );
                                }

                                break;
                            default:
                                if (!input.value) {
                                    empty.push(input);
                                    input.classList.add(
                                        'subject-finder__form-error'
                                    );
                                }
                        }
                    }
                });
            });

            formErrorMsg.textContent = 'Please select one of the options.';

            this._updateState();
        } else {
            requiredNames.forEach((reqName) => {
                const inputs = document.querySelectorAll(`[name="${reqName}"]`);

                inputs.forEach((input) => {
                    switch (input.type) {
                        case 'radio':
                        case 'checkbox':
                            input.parentNode.classList.remove(
                                'subject-finder__form-error'
                            );

                            break;
                        default:
                            input.classList.remove(
                                'subject-finder__form-error'
                            );
                    }
                });
            });

            this.paused(false, false);

            this._updateState();

            document.removeEventListener('change', this._globalInputValidate);
            document.removeEventListener('keyup', this._globalInputValidate);

            formErrorMsg.textContent = '';
        }

        // this.paused(false, false);

        // document.removeEventListener('change', this._globalInputValidate);
        // document.removeEventListener('keyup', this._globalInputValidate);
    }

    _globalInputValidate = () => {
        this.validate();
    };

    _onValidateChange = () => {
        this.validate();
    };

    /**
     * a loop that goes through each
     * page or section and spits out
     * info about them
     *
     * @param {string} type
     * what type to get (page or section)
     *
     * to get pages: "page" or "pages" or "step" or "steps"
     *
     * to get sections: "section" or "sections"
     *
     * @param {callable} callback
     * the callback will execute on each iteration
     * of the loop and will take a single param
     * which will provide info about the page/section
     *
     * @param {bool} includeExcluded
     * this will include excluded content
     * from the each loop
     *
     * @return {void}
     */
    each(type, callback, includeExcluded) {
        if (type.match(/page|pages|step|steps/g)) {
            const data = includeExcluded ? 'pages' : 'steps';

            this[data].forEach((page) => {
                if (callback) {
                    callback({
                        page,
                        ...page.dataset,
                    });
                }
            });
        } else if (type.match(/section|sections/g)) {
            this.sections.forEach((section) => {
                if (
                    !includeExcluded &&
                    !this.excludedSections.includes(section.dataset.name)
                ) {
                    if (callback) {
                        callback({
                            section,
                            ...section.dataset,
                        });
                    }
                }

                if (includeExcluded) {
                    if (callback) {
                        callback({
                            section,
                            ...section.dataset,
                        });
                    }
                }
            });
        }
    }

    /**
     * tracks all actions of the application
     *
     * @param {callable} callback
     * this will fire on each interaction of
     * the application, including once it
     * loads
     *
     * @return {void}
     */
    observer(callback) {
        this.observerCallback = callback;
    }

    /**
     * gets the navigation controls all controls
     * are prefixed with an _
     *
     * @return {object}
     */
    getControls() {
        return this.controlsKeyVal;
    }

    /**
     * excludes steps from the process
     *
     * @param {object} params
     *
     * params.sections = the sections to exclude
     * from the process
     *
     * params.pages = the pages to exclude froom
     * the process
     */
    exclude(params) {
        if (params) {
            let { sections, pages } = params;

            this.steps = [...this.pages];

            // prevent section duplicates
            if (sections) {
                if (this.excludedSections) {
                    sections = sections.filter((section) => {
                        if (!this.excludedSections.includes(section))
                            return section;

                        return null;
                    });
                }

                this.excludedSections = this.excludedSections
                    ? [...this.excludedSections, ...sections]
                    : sections;
            }

            // prevent page duplicates
            if (pages) {
                if (this.excludedPages) {
                    pages = pages.filter((page) => {
                        if (!this.excludedPages.includes(page)) return page;

                        return null;
                    });
                }

                this.excludedPages = this.excludedPages
                    ? [...this.excludedPages, ...pages]
                    : pages;
            }
        }

        if (!this.excludedSections && !this.excludedPages) {
            this.steps = [...this.pages];
        }

        this.steps = [...this.pages];

        if (this.excludedSections) {
            this.steps = this.steps.filter((step) => {
                if (!this.excludedSections.includes(step.dataset.section)) {
                    return step;
                }

                return null;
            });

            this.isLast = this.current === this.steps.length - 1;
            this.isFirst = this.current === 0;
        }

        if (this.excludedPages) {
            this.steps = this.steps.filter((step) => {
                if (!this.excludedPages.includes(step.dataset.name)) {
                    return step;
                }

                return null;
            });

            this.isLast = this.current === this.steps.length - 1;
            this.isFirst = this.current === 0;
        }

        this._updateState();
    }

    /**
     * includes pages/sections that have been
     * previously excluded
     *
     * @param {object} params
     *
     * params.pages {array} an array of page names
     * to include back into the subject finder
     *
     * params.sections {array} an array of sections
     * to include back into the subject finder
     *
     * @return {void}
     */
    include(params) {
        if (params) {
            const { sections, pages } = params;

            if (sections) {
                const indexes = [];

                sections.forEach((page) => {
                    if (!this.excludedSections) return;

                    const excludedIndex = this.excludedSections.indexOf(page);

                    if (excludedIndex > -1) {
                        indexes.push(excludedIndex);
                    }
                });

                if (this.excludedSections) {
                    this.excludedSections = this.excludedSections.filter(
                        (page, i) => !indexes.includes(i)
                    );
                }

                this.excludedSections =
                    this.excludedSections || !this.excludedSections.length
                        ? null
                        : this.excludedSections;
            }

            if (pages) {
                const indexes = [];

                pages.forEach((page) => {
                    if (!this.excludedPages) return;

                    const excludedIndex = this.excludedPages.indexOf(page);

                    if (excludedIndex > -1) {
                        indexes.push(excludedIndex);
                    }
                });

                if (this.excludedPages) {
                    this.excludedPages = this.excludedPages.filter(
                        (page, i) => !indexes.includes(i)
                    );
                }

                this.excludedPages =
                    !this.excludedPages || !this.excludedPages.length
                        ? null
                        : this.excludedPages;
            }

            this.exclude();
            // this._setActiveNavState();
            // this._subPageNumber();
        }
    }

    /**
     * sets the active state of a control
     *
     * @param {string} name
     * the control's [data-nav] name
     *
     * @param {bool} state
     * true = active
     * false = inactive
     *
     * @return {void}
     */
    setControlState(state, ...names) {
        const isActive = !state ? 'add' : 'remove';

        names.forEach((controlName) => {
            if (this.controlsKeyVal[`_${controlName}`]) {
                this.controlsKeyVal[`_${controlName}`].parentNode.classList[
                    isActive
                ]('hidden');
                this.controlsKeyVal[`_${controlName}`].classList[isActive](
                    'hidden'
                );
            }
        });
    }

    /**
     * sets the start point of the progress
     * steps
     *
     * @param {object} params
     *
     * params.section = the section to start from
     *
     * params.page = the page to start from
     *
     * @return {void}
     */
    setStart(params, goTo) {
        if (params) {
            const { section, page } = params;

            if (section) this.startSection = section;
            if (page) this.startPage = page;
        }

        if (this.startSection) {
            for (let i = 0; i < this.steps.length; i++) {
                if (this.steps[i].dataset.section === this.startSection) {
                    this.current = i;

                    this.isLast = this.current === this.steps.length - 1;
                    this.isFirst = this.current === 0;

                    if (goTo) this._activate();

                    this._updateState();
                    return;
                }
            }
        }

        if (this.startPage) {
            for (let i = 0; i < this.steps.length; i++) {
                if (this.steps[i].dataset.name === this.startPage) {
                    this.current = i;

                    this.isLast = this.current === this.steps.length - 1;
                    this.isFirst = this.current === 0;

                    if (goTo) this._activate();

                    this._updateState();
                    return;
                }
            }
        }
    }

    /**
     * pauses the progress from the continue
     * button perspective
     *
     * @param {bool} pauseState
     * whether or noth the continue step is
     * paused or not
     *
     * if the above param is not filled then
     * it's assumed the pause state is off
     * and progress will continue
     *
     * @return {void}
     */
    paused(pauseState, autoStep) {
        const advanceStep = autoStep === undefined ? true : autoStep;

        this.pause = pauseState;

        this.controls.forEach((control) => {
            const controlItem = control;

            controlItem.disabled = true;
        });

        if (!pauseState) {
            if (advanceStep) this._continueProgress();

            this.controls.forEach((control) => {
                const controlItem = control;

                controlItem.disabled = false;
            });
        }
    }

    /// ///////////////////////////////////////
    // -- public events

    /**
     * when any of the control buttons
     * are clicked this event will fire
     */
    onAction(callback) {
        this.onActionCallback = callback;
    }

    /**
     * when the continue button is clicked
     * and the step has changed fire the
     * continue event
     */
    onContinueAfter(callback) {
        this.onContinueAfterCallback = callback;
    }

    /**
     * when the continue button is clicked
     * and the step is about to change fire
     * the continue event
     */
    onContinueBefore(callback) {
        this.onContinueBeforeCallback = callback;
    }

    /**
     * when the restart button is clicked
     */
    onRestart(callback) {
        this.onRestartCallback = callback;
    }

    /**
     * this will fire just like the
     * onContinueAfter, except this will also
     * fire when a pause has been unpaused
     *
     * this is to prevent any sensitive code
     * onContinueAfter from running multiple times
     */
    onProgress(callback) {
        this.onProgressCallback = callback;
    }

    /**
     * this will fire when the app has initialised
     */
    onStart(callback) {
        this.onStartCallback = callback;
    }

    /**
     * this will fire when using the steps
     * to navigate, it will get page data
     * before the page has changed
     */
    onNavigateBefore(callback) {
        this.onNavigateBeforeCallback = callback;
    }

    /**
     * this will fire when using the steps
     * to navigate, it will get page data
     * after the page has changed
     */
    onNavigateAfter(callback) {
        this.onNavigateAfterCallback = callback;
    }

    /**
     * this will fire when using the back button
     */
    onBack(callback) {
        this.onBackCallback = callback;
    }

    /**
     * this will fire when the skip button is clicked
     */
    onSkip(callback) {
        this.onSkipCallback = callback;
    }

    /**
     * this will fire before the skip has been finished
     */
    onSkipBefore(callback) {
        this.onSkipBeforeCallback = callback;
    }

    /// ///////////////////////////////////////
    // -- private

    /**
     * when the pause state is unpaused
     * this method is called to continue to the
     * next step
     */
    _continueProgress() {
        // this._updateSectionInfo();
        this._next();
        this._activate();
        this._subPageNumber();
        this._updateState();

        if (this.onProgressCallback) this.onProgressCallback();
    }

    /**
     * this method is called to update data
     * about sections, such as the number of
     * pages per section (as some can be filtered out)
     * what sections are visible what the index of each
     * page is, in context to the section
     *
     * @return {void}
     */
    _updateSectionInfo() {
        this.sections.forEach((section) => {
            const sectionName = section.dataset.name;

            [...section.children].forEach((child) => {
                const childNode = child;

                childNode.dataset.section = sectionName;
            });
        });

        this.sections.forEach((section) => {
            const { name, title } = section.dataset;

            this.sectionsKeyVal[name] = section;

            this.sectionsInfo[name] = {};
            this.sectionsInfo[name].title = title || name;

            const pages = this.steps.filter((step, i) => {
                const stepItem = step;

                if (stepItem.dataset.section === name) {
                    stepItem.dataset.index = i;

                    return stepItem;
                }

                return null;
            });

            // set number of pages in a section
            this.sectionsInfo[name].pageCount = pages.length;
            this.sectionsInfo[name].pages = pages;
        });
    }

    /**
     * updates the overall state of various
     * parts of the class, such as:
     * - what pages / sections are active and are tracked
     * - what progress bar item is currently active
     * - what is the active sub page number in that step
     *
     * @return {void}
     */
    _updateState() {
        this._updateSectionInfo();
        this._subPageNumber();
        this._setActiveNavState();
    }

    /**
     * activates a step
     *
     * @return {void}
     */
    _activate() {
        this.pages.forEach((step) => {
            step.classList.remove('active');
        });

        this.sections.forEach((section) => {
            section.classList.remove('active');
        });

        if (!this.steps[this.current]) return;

        const pageSection = this.steps[this.current].dataset.section;

        this.sectionsKeyVal[pageSection].classList.add('active');
        this.steps[this.current].classList.add('active');

        this.active = this.steps[this.current];
        this.activeData = this.steps[this.current].dataset;
        this.currentSection = this.sectionsKeyVal[pageSection];
        this.currentSectionData = this.sectionsKeyVal[pageSection].dataset;

        if (!this.isFirst) {
            this.lastStepData = this.steps[this.current - 1].dataset;
        }
    }

    /**
     * build out the progress bar
     *
     * @return {void}
     */
    _setNavigation() {
        const navContainer = document.querySelector(`[data-role="navigation"]`);

        if (!navContainer) return;

        navContainer.innerHTML = '';

        Object.keys(this.sectionsInfo).forEach((section) => {
            if (
                this.excludedSections &&
                this.excludedSections.includes(section)
            )
                return;

            const navButtonListItem = document.createElement('li');
            const navButton = document.createElement('button');
            const { pageCount, title } = this.sectionsInfo[section];

            navButtonListItem.classList.add(
                'subject-finder__progress-bar__item'
            );
            navButtonListItem.append(navButton);

            navButton.classList.add(
                'subject-finder__progress-bar__section-btn'
            );
            navButton.innerHTML = title;

            this.navButtons[section] = navButton;

            navButton.addEventListener(
                'click',
                this._onNavigate.bind(this, section)
            );
            navContainer.append(navButtonListItem);
        });
    }

    /**
     * show sub pages in the currently
     * active navigation item
     *
     * @return {void}
     */
    _subPageNumber() {
        if (!this.activeData) return;

        const { section } = this.activeData;
        const { pageCount, pages } = this.sectionsInfo[section];

        if (this.pageNumber) this.pageNumber.remove();

        if (pageCount > 1) {
            if (this.pageNumber) this.pageNumber.remove();

            const currentButton = this.navButtons[section];
            const pageIndex = pages.indexOf(this.active);

            this.pageNumber.innerHTML = `<span class="subject-finder__progress-bar__pages">${
                pageIndex > -1 ? pageIndex + 1 : 1
            }/${pageCount}</span>`;

            currentButton.append(this.pageNumber);
        }
    }

    /**
     * sets the currently active navigation
     * state
     */
    _setActiveNavState() {
        if (!this.activeData) return;

        const { section } = this.activeData;

        Object.keys(this.navButtons).forEach((sectionKey) => {
            this.navButtons[sectionKey].classList.remove('active');
            this.navButtons[sectionKey].disabled = false;
        });

        this.navButtons[section].classList.add('active');
        this.navButtons[section].disabled = true;
    }

    /**
     * advances the step index
     *
     * @return {void}
     */
    _next() {
        this.current =
            this.current === this.steps.length - 1
                ? this.steps.length - 1
                : this.current + 1;

        this.isFirst = this.current === 0;
        this.isLast = this.current === this.steps.length - 1;
    }

    /**
     * subtracts the step index
     *
     * @return {void}
     */
    _prev() {
        this.current = this.current === 0 ? 0 : this.current - 1;

        this.isFirst = this.current === 0;
        this.isLast = this.current === this.steps.length - 1;
    }

    /// ///////////////////////////////////////
    // -- events

    /**
     * when clicking on the progress
     * navigation steps
     *
     * @param {string} section
     * the current section
     */
    _onNavigate(section, e) {
        if (this.onNavigateBeforeCallback) this.onNavigateBeforeCallback();

        if (window.nsnRetrieved && section === 'academic') {
            setTimeout(() => {
                this.setStart(
                    {
                        page: 'nsn-api-best-subject',
                    },
                    true
                );
            }, 0);
        }

        this.setStart(
            {
                section,
            },
            true
        );
        // this._activate();
        this._updateState();
        // this._setActiveNavState();
        // this._subPageNumber();

        this.startSection = null;
        this.startPage = null;

        this.isLast = this.current === this.steps.length - 1;
        this.isFirst = this.current === 0;

        if (this.onNavigateAfterCallback) this.onNavigateAfterCallback();

        if (this.observerCallback) this.observerCallback();
    }

    /**
     * when clicking the back button
     * go back to the previous step
     */
    _back() {
        this._prev();
        this._activate();
        this._updateState();

        if (this.onBackCallback) this.onBackCallback();

        // this._setActiveNavState();
        // this._subPageNumber();
    }

    /**
     * when clicking the skip button
     * go to the next step
     */
    _skip() {
        if (this.onSkipBeforeCallback) this.onSkipBeforeCallback();

        this._next();
        this._activate();
        this._updateState();

        if (this.onSkipCallback) this.onSkipCallback();

        // this._subPageNumber();
    }

    /**
     * when clicking the continue button
     * go to the next step
     */
    _continue(e) {
        const { currentTarget } = e;

        if (this.onContinueBeforeCallback) this.onContinueBeforeCallback();

        if (this.pause) return;

        this._next();
        this._activate();
        this._updateState();
        // this._setActiveNavState();
        // this._subPageNumber();

        if (this.onProgressCallback) this.onProgressCallback();

        if (this.onContinueAfterCallback) this.onContinueAfterCallback();
    }

    /**
     * when clicking the restart button
     */
    _restart() {
        if (this.onRestartCallback) this.onRestartCallback();
    }

    /**
     * when any of the control buttons
     * are clicked this event will call the
     * public facing event if it's defined
     */
    _onAction(e) {
        this._setActiveNavState();
        this._updateState();
        // this._subPageNumber();

        if (this.onActionCallback) this.onActionCallback();
        if (this.observerCallback) this.observerCallback();
    }

    /**
     * takes the user back to the questions
     * section of the subject finder process
     */
    _retake() {
        this.setStart(
            {
                section: 'questions',
            },
            true
        );

        this._updateState();

        this.startSection = null;
        this.startPage = null;

        this.isLast = this.current === this.steps.length - 1;
        this.isFirst = this.current === 0;
    }

    /**
     * initialize the application
     */
    init() {
        this.setStart();
        this.exclude();
        this._activate();
        this._setNavigation();
        this._updateState();

        // this._updateSectionInfo();
        // this._setActiveNavState();
        // this._subPageNumber();

        if (this.observerCallback) this.observerCallback();
        if (this.onStartCallback) this.onStartCallback();
    }
}
